import { HttpBackend, HttpClient } from '@angular/common/http';
import { importProvidersFrom } from '@angular/core';
import { Routes } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { AuthGuardService } from '@shared/services/auth-guard.service';
import { ManagedStoreService } from '@shared/services/managed-store.service';
import { UnleashProxyService } from '@shared/services/unleash-proxy.service';

import { CashSettlementService } from './feature/cash-settlement/service/cash-settlement.service';
import { CashSettlementEffects } from './feature/cash-settlement/store/cash-settlement.effects';
import { cashSettlementReducer } from './feature/cash-settlement/store/cash-settlement.reducers';
import { ClaimService } from './feature/claim/services/claim.service';
import { ClaimEffects } from './feature/claim/store/claim.effects';
import { claimKey, claimReducer } from './feature/claim/store/claim.reducers';
import { DestroyPermission } from './feature/destroy/destroy-permission';
import { DestroyService } from './feature/destroy/services/destroy.service';
import { DestroyEffects } from './feature/destroy/store/destroy.effects';
import { destroyReducer } from './feature/destroy/store/destroy.reducers';
import { MainPagePermissionService } from './feature/main-page/main-page-permission.service';
import { MainPageService } from './feature/main-page/services/main-page.service';
import { MainPageEffects } from './feature/main-page/store/main-page/main-page.effects';
import { MainPageReducer } from './feature/main-page/store/main-page/main-page.reducers';
import { MaterialService } from './feature/material/service/material.service';
import { MaterialRequestListEffects } from './feature/material/store/material-request-list/material-request-list.effects';
import { materialRequestListReducer } from './feature/material/store/material-request-list/material-request-list.reducers';
import { MemberRewardPickupService } from './feature/member-reward-pickup/services/member-reward-pickup.service';
import { MemberRewardPickupEffects } from './feature/member-reward-pickup/stores/member-reward-pickup/member-reward-pickup.effects';
import {
  memberRewardPickupKey,
  memberRewardPickupReducer
} from './feature/member-reward-pickup/stores/member-reward-pickup/member-reward-pickup.reducer';
import { MoneyInOutService } from './feature/money-in-out/service/money-in-out.service';
import { MoneyInOutEffects } from './feature/money-in-out/store/money-in-out.effects';
import { moneyInOutReducer } from './feature/money-in-out/store/money-in-out.reducers';
import { RelayPrinterService } from './feature/product-tag/services/relay-printer.service';
import { ReceiveOrderService } from './feature/receive-order/services/receive-order.service';
import { ReceiveOrderEffects } from './feature/receive-order/store/receive-order.effects';
import { receiveOrderKey, receiveOrderReducer } from './feature/receive-order/store/receive-order.reducers';
import { ReduceToClearPermission } from './feature/reduce-to-clear/reduce-to-clear-permission';
import { ReduceToClearRequestService } from './feature/reduce-to-clear/service/reduce-to-clear-request.service';
import { ReduceToClearService } from './feature/reduce-to-clear/service/reduce-to-clear.service';
import { ReduceToClearEffects } from './feature/reduce-to-clear/store/reduce-to-clear/reduce-to-clear.effects';
import { reduceToClearReducer } from './feature/reduce-to-clear/store/reduce-to-clear/reduce-to-clear.reducers';
import { ReduceToClearRequestEffects } from './feature/reduce-to-clear/store/reduce-to-clear-request/reduce-to-clear-request.effects';
import { reduceToClearRequestReducer } from './feature/reduce-to-clear/store/reduce-to-clear-request/reduce-to-clear-request.reducers';
import { ReportService } from './feature/report/services/report.service';
import { MyReportEffects } from './feature/report/stores/my-report/my-report.effects';
import { myReportReducer } from './feature/report/stores/my-report/my-report.reducer';
import { ReportsEffects } from './feature/report/stores/reports/reports.effects';
import { reportReducer } from './feature/report/stores/reports/reports.reducer';
import { ReturnToSupplierHistoryService } from './feature/return-to-supplier-history/service/return-to-supplier-history.service';
import { ReturnToSupplierHistoryEffects } from './feature/return-to-supplier-history/store/return-to-supplier-history/return-to-supplier-history.effects';
import { ReturnToSupplierHistoryReducer } from './feature/return-to-supplier-history/store/return-to-supplier-history/return-to-supplier-history.reducers';
import { returnToSupplierHistoryKey } from './feature/return-to-supplier-history/store/return-to-supplier-history/return-to-supplier-history.selectors';
import { ShelfService } from './feature/shelf/service/shelf.service';
import { ShelfEffects } from './feature/shelf/store/shelf.effects';
import { shelfReducer } from './feature/shelf/store/shelf.reducers';
import { ShiftService } from './feature/shift/service/shift.service';
import { ShiftEffects } from './feature/shift/store/shift.effects';
import { shiftReducer } from './feature/shift/store/shift.reducers';
import { StockService } from './feature/stock/service/stock.service';
import { StockEffects } from './feature/stock/store/stock.effects';
import { stockKey, stockReducer } from './feature/stock/store/stock.reducers';
import { StockCheckHistoryService } from './feature/stock-check-history/service/stock-check-history.service';
import { StockCheckHistoryEffects } from './feature/stock-check-history/store/stock-check-history/stock-check-history.effects';
import { StockCheckHistoryReducer } from './feature/stock-check-history/store/stock-check-history/stock-check-history.reducers';
import { stockCheckHistoryKey } from './feature/stock-check-history/store/stock-check-history/stock-check-history.selectors';
import { StockCollectionHistoryService } from './feature/stock-collection-history/service/stock-collection-history.service';
import { StockCollectionHistoryEffects } from './feature/stock-collection-history/store/stock-collection-history/stock-collection-history.effects';
import { StockCollectionHistoryReducer } from './feature/stock-collection-history/store/stock-collection-history/stock-collection-history.reducers';
import { stockCollectionHistoryKey } from './feature/stock-collection-history/store/stock-collection-history/stock-collection-history.selectors';
import { StockCountHistoryService } from './feature/stock-count-history/service/stock-count-history.service';
import { StockCountHistoryPermission } from './feature/stock-count-history/stock-count-history-permission';
import { StockCountHistoryEffects } from './feature/stock-count-history/store/stock-count-history/stock-count-history.effects';
import { StockCountHistoryReducer } from './feature/stock-count-history/store/stock-count-history/stock-count-history.reducers';
import { StockPackingHistoryService } from './feature/stock-packing-history/service/stock-packing-history.service';
import { StockPackingHistoryEffects } from './feature/stock-packing-history/store/stock-packing-history/stock-packing-history.effects';
import { StockPackingHistoryReducer } from './feature/stock-packing-history/store/stock-packing-history/stock-packing-history.reducers';
import { stockPackingHistoryKey } from './feature/stock-packing-history/store/stock-packing-history/stock-packing-history.selectors';
import { StockToteService } from './feature/stock-tote/service/stock-tote.service';
import { StockToteEffects } from './feature/stock-tote/store/stock-tote.effects';
import { stockToteKey, stockToteReducer } from './feature/stock-tote/store/stock-tote.reducers';
import { StoreAvailableAdjustmentRequestService } from './feature/store-available-adjustment/service/store-available-adjustment-request.service';
import { StoreAvailableAdjustmentRequestEffects } from './feature/store-available-adjustment/store/store-available-adjustment-request/store-available-adjustment-request.effects';
import { storeAvailableAdjustmentRequestReducer } from './feature/store-available-adjustment/store/store-available-adjustment-request/store-available-adjustment-request.reducers';
import { StoreExpenseService } from './feature/store-expense/service/store-expense.service';
import { StoreExpenseEffects } from './feature/store-expense/store/store-expense.effects';
import { storeExpenseReducer } from './feature/store-expense/store/store-expense.reducers';
import { SupplierPartnerCouponService } from './feature/supplier-partner-coupon/services/supplier-partner-coupon.service';
import { SupplierPartnerCouponEffects } from './feature/supplier-partner-coupon/store/supplier-partner-coupon.effects';
import {
  supplierPartnerCouponFeatureKey,
  supplierPartnerCouponReducer
} from './feature/supplier-partner-coupon/store/supplier-partner-coupon.reducer';
import { TaskAssignmentPermission } from './feature/task-assignment/task-assignment-permission';
import { TaxInvoiceService } from './feature/tax-invoice/service/tax-invoice.service';
import { TaxInvoiceEffects } from './feature/tax-invoice/store/tax-invoice.effects';
import { taxInvoiceReducer } from './feature/tax-invoice/store/tax-invoice.reducers';
import { AppLayoutComponent } from './layout/app.layout.component';
import { LoginLayoutComponent } from './layout/login-layout/login-layout.component';

export const APP_ROUTES: Routes = [
  {
    path: '',
    component: LoginLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () => import('./feature/login/login.component').then((mod) => mod.LoginComponent)
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import('./feature/change-password/change-password.component').then((mod) => mod.ChangePasswordComponent)
      },
      {
        path: 'managed-store',
        providers: [ManagedStoreService],
        loadComponent: () =>
          import('./feature/managed-store/managed-store.component').then((mod) => mod.ManagedStoreComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./feature/forgot-password/forgot-password.component').then((mod) => mod.ForgotPasswordComponent)
      }
    ]
  },
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      // {
      //   path: 'dashboard',
      //   data: {
      //     title: 'MAIN',
      //     isShowOnMenu: true,
      //     icon: 'pi pi-home',
      //     permissions: ['role_m', 'role_vx', 'posm_rc_v'],
      //   },
      //   loadChildren: () =>
      //     import('./feature/dashboard/dashboards.module').then(
      //       (m) => m.DashboardsModule
      //     ),
      // },
      // {
      //   path: 'sales',
      //   data: {
      //     title: 'SALE',
      //     isShowOnMenu: true,
      //     icon: 'icon-promotion',
      //   },
      //   children: [
      //     {
      //       path: 'total-sales',
      //       data: {
      //         isShowOnMenu: true,
      //         title: 'TOTAL_SALES',
      //         permissions: ['role_m', 'role_vx', 'posm_rc_v'],
      //       },
      //       canActivate: [AuthGuardService],
      //       providers: [
      //         ManagedStoreService,
      //         importProvidersFrom(
      //           EffectsModule.forFeature([ManagedStoreEffects]),
      //           StoreModule.forFeature('managdeStore', managedStoreReducer)
      //         ),
      //       ],
      //       loadChildren: () => import('./feature/central-announcement/central-announcement-routing').then((mod) => mod.CENTRAL_ROUTES)
      //     },
      //   ],
      // },
      // {
      //   path: 'stock',
      //   data: {
      //     title: 'STOCK',
      //     isShowOnMenu: true,
      //     icon: 'icon-stock',
      //   },
      //   children: [
      //     {
      //       path: 'total-stock',
      //       data: {
      //         isShowOnMenu: true,
      //         title: 'TOTAL_STOCK',
      //         permissions: ['role_m', 'role_vc', 'posm_rc_v'],
      //       },
      //       canActivate: [AuthGuardService],
      //       providers: [
      //         ManagedStoreService,
      //         importProvidersFrom(
      //           EffectsModule.forFeature([ManagedStoreEffects]),
      //           StoreModule.forFeature('managdeStore', managedStoreReducer)
      //         ),
      //       ],
      //       loadComponent: () =>
      //         import(
      //           './feature/central-announcement/central-announcement.component'
      //         ).then((m) => m.CentralAnnouncementComponent),
      //     },
      //   ],
      // },
      // {
      //   path: 'dashboard',
      //   data: {
      //     title: 'MAIN',
      //     isShowOnMenu: true,
      //     icon: 'pi pi-home',
      //     permissions: ['role_m', 'role_vx', 'posm_rc_v'],
      //   },
      //   loadChildren: () =>
      //     import('./feature/dashboard/dashboards.module').then(
      //       (m) => m.DashboardsModule
      //     ),
      // },
      // {
      //   path: 'sales',
      //   data: {
      //     title: 'SALE',
      //     isShowOnMenu: true,
      //     icon: 'pi pi-shopping-cart',
      //   },
      //   children: [
      //     {
      //       path: 'total-sales',
      //       data: {
      //         isShowOnMenu: true,
      //         title: 'TOTAL_SALES',
      //         permissions: ['role_m', 'role_vx', 'posm_rc_v'],
      //       },
      //       canActivate: [AuthGuardService],
      //       providers: [
      //         ManagedStoreService,
      //         importProvidersFrom(
      //           EffectsModule.forFeature([ManagedStoreEffects]),
      //           StoreModule.forFeature('managdeStore', managedStoreReducer)
      //         ),
      //       ],
      //       loadChildren: () => import('./feature/central-announcement/central-announcement-routing').then((mod) => mod.CENTRAL_ROUTES)
      //     },
      //   ],
      // }, {
      {
        path: 'main',
        data: {
          title: 'MAIN',
          isShowOnMenu: true,
          icon: 'pi pi-home'
        },
        canActivate: [AuthGuardService],
        providers: [
          MainPageService,
          MainPagePermissionService,
          UnleashProxyService,
          importProvidersFrom(
            EffectsModule.forFeature([MainPageEffects]),
            StoreModule.forFeature('mainPageStore', MainPageReducer)
          )
        ],
        loadChildren: () => import('./feature/main-page/main-page.routing').then((mod) => mod.CENTRAL_ROUTES)
      },
      {
        path: '',
        data: {
          title: 'SALE',
          isShowOnMenu: true,
          icon: 'icon-sales',
          permissions: [
            'pm_receipt_v',
            'pm_receipt_m',
            'pm_shift_v',
            'pm_shift_m',
            'pm_cash_settle_v',
            'pm_cash_settle_m'
          ]
        },
        children: [
          {
            path: 'tax-invoice',
            data: {
              isShowOnMenu: true,
              title: 'TAX_INVOICE',
              permissions: ['pm_receipt_v', 'pm_receipt_m']
            },
            canActivate: [AuthGuardService],
            providers: [
              TaxInvoiceService,
              importProvidersFrom(
                EffectsModule.forFeature([TaxInvoiceEffects]),
                StoreModule.forFeature('taxInvoice', taxInvoiceReducer)
              )
            ],
            loadChildren: () => import('./feature/tax-invoice/tax-invoice-routing').then((mod) => mod.CENTRAL_ROUTES)
          },
          {
            path: 'shift',
            data: {
              isShowOnMenu: true,
              title: 'SHIFT',
              permissions: ['pm_shift_v', 'pm_shift_m'],
              featureName: ['shift.open-close-with-penalty-scan', 'shift.open-close-summary']
            },
            canActivate: [AuthGuardService],
            providers: [
              ShiftService,
              importProvidersFrom(
                EffectsModule.forFeature([ShiftEffects]),
                StoreModule.forFeature('shift', shiftReducer)
              )
            ],
            loadChildren: () => import('./feature/shift/shift-routing').then((mod) => mod.CENTRAL_ROUTES)
          },
          {
            path: 'money-in-out',
            data: {
              isShowOnMenu: true,
              title: 'MONEY_IN_OUT',
              permissions: ['pm_money_v'],
              featureName: ['shift.open-close-with-penalty-scan']
            },
            canActivate: [AuthGuardService],
            providers: [
              MoneyInOutService,
              importProvidersFrom(
                EffectsModule.forFeature([MoneyInOutEffects]),
                StoreModule.forFeature('money-in-out', moneyInOutReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/money-in-out/money-in-out-routing').then((mod) => mod.CENTRAL_ROUTES)
          },
          {
            path: 'cash-settlement',
            data: {
              isShowOnMenu: true,
              title: 'CASH_SETTLEMENT',
              permissions: ['pm_cash_settle_v', 'pm_cash_settle_m']
            },
            canActivate: [AuthGuardService],
            providers: [
              CashSettlementService,
              importProvidersFrom(
                EffectsModule.forFeature([CashSettlementEffects]),
                StoreModule.forFeature('cashSettlement', cashSettlementReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/cash-settlement/cash-settlement-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'STORE_EXPENSE',
          isShowOnMenu: true,
          icon: 'icon-store-expense',
          permissions: ['pm_store_exp_req_v', 'pm_store_exp_req_m']
        },
        children: [
          {
            path: 'store-expense',
            data: {
              isShowOnMenu: true,
              title: 'STORE_EXPENSE',
              permissions: ['pm_store_exp_req_v', 'pm_store_exp_req_m']
            },
            canActivate: [AuthGuardService],
            providers: [
              StoreExpenseService,
              importProvidersFrom(
                EffectsModule.forFeature([StoreExpenseEffects]),
                StoreModule.forFeature('storeExpense', storeExpenseReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/store-expense/store-expense-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'MATERIAL',
          isShowOnMenu: true,
          icon: 'icon-material',
          permissions: ['pm_mr_v']
        },
        children: [
          {
            path: 'material',
            data: {
              isShowOnMenu: true,
              title: 'MATERIAL_HISTORY',
              permissions: ['pm_mr_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              MaterialService,
              importProvidersFrom(
                EffectsModule.forFeature([MaterialRequestListEffects]),
                StoreModule.forFeature('materialRequestListKey', materialRequestListReducer)
              )
            ],
            loadChildren: () => import('./feature/material/material-routing.module').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'STOCK',
          isShowOnMenu: true,
          icon: 'icon-stock',
          permissions: ['pm_stock_v', 'pm_stock_m']
        },
        children: [
          {
            path: 'stock',
            data: {
              isShowOnMenu: true,
              title: 'STOCK_STORE',
              permissions: ['pm_stock_v', 'pm_stock_m']
            },
            canActivate: [AuthGuardService],
            providers: [
              StockService,
              importProvidersFrom(
                EffectsModule.forFeature([StockEffects]),
                StoreModule.forFeature(stockKey, stockReducer)
              )
            ],
            loadChildren: () => import('./feature/stock/stock-routing').then((mod) => mod.CENTRAL_ROUTES)
          },
          {
            path: 'stock-count-history',
            data: {
              isShowOnMenu: true,
              title: 'STOCK_COUNT_HISTORY',
              permissions: ['pm_stkcnt_his_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              StockCountHistoryService,
              importProvidersFrom(
                EffectsModule.forFeature([StockCountHistoryEffects]),
                StoreModule.forFeature('stockCountHistory', StockCountHistoryReducer)
              ),
              StockCountHistoryPermission
            ],
            loadChildren: () =>
              import('./feature/stock-count-history/stock-count-history-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          },
          {
            path: 'store-available-adjustment',
            data: {
              isShowOnMenu: true,
              title: 'STORE_AVAILABLE_ADJUSTMENT',
              permissions: ['pm_store_avai_adj_req_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              StoreAvailableAdjustmentRequestService,
              importProvidersFrom(
                EffectsModule.forFeature([StoreAvailableAdjustmentRequestEffects]),
                StoreModule.forFeature('storeAvailableAdjustmentRequest', storeAvailableAdjustmentRequestReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/store-available-adjustment/store-available-adjustment-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'STOCK_TOTE',
          isShowOnMenu: true,
          icon: 'icon-stock-tote',
          permissions: ['pm_stock_tote_v', 'pm_stock_tote_m']
        },
        children: [
          {
            path: 'stock-tote',
            data: {
              isShowOnMenu: true,
              title: 'STOCK_TOTE',
              permissions: ['pm_stock_tote_v', 'pm_stock_tote_m']
            },
            canActivate: [AuthGuardService],
            providers: [
              StockToteService,
              importProvidersFrom(
                EffectsModule.forFeature([StockToteEffects]),
                StoreModule.forFeature(stockToteKey, stockToteReducer)
              )
            ],
            loadChildren: () => import('./feature/stock-tote/stock-tote-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'ORDER',
          isShowOnMenu: true,
          icon: 'icon-inbound',
          data: {
            permissions: ['pm_ro_v', 'pm_cr_v']
          }
        },
        children: [
          {
            path: 'receive-order',
            data: {
              isShowOnMenu: true,
              title: 'RECEIVE_ORDER',
              permissions: ['pm_ro_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              ReceiveOrderService,
              importProvidersFrom(
                EffectsModule.forFeature([ReceiveOrderEffects]),
                StoreModule.forFeature(receiveOrderKey, receiveOrderReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/receive-order/receive-order-routing').then((mod) => mod.CENTRAL_ROUTES)
          },
          {
            path: 'claim',
            data: {
              isShowOnMenu: true,
              title: 'CLAIM',
              permissions: ['pm_cr_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              ClaimService,
              importProvidersFrom(
                EffectsModule.forFeature([ClaimEffects]),
                StoreModule.forFeature(claimKey, claimReducer)
              )
            ],
            loadChildren: () => import('./feature/claim/claim-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'STORE_STOCK_RECALL',
          isShowOnMenu: true,
          icon: 'icon-recall',
          data: {
            permissions: ['pm_recall_stkchk_v', 'pm_recall_stkpack_v', 'pm_recall_stkcoll_v']
          }
        },
        children: [
          {
            path: 'stock-check-history',
            data: {
              isShowOnMenu: true,
              title: 'STOCK_CHECK_HISTORY',
              permissions: ['pm_recall_stkchk_v'],
              featureName: ['store-stock-recall.warehouse-recall-mgt']
            },
            canActivate: [AuthGuardService],
            providers: [
              StockCheckHistoryService,
              importProvidersFrom(
                EffectsModule.forFeature([StockCheckHistoryEffects]),
                StoreModule.forFeature(stockCheckHistoryKey, StockCheckHistoryReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/stock-check-history/stock-check-history-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          },
          {
            path: 'stock-packing-history',
            data: {
              isShowOnMenu: true,
              title: 'STOCK_PACKING_HISTORY',
              permissions: ['pm_recall_stkpack_v'],
              featureName: ['store-stock-recall.warehouse-recall-mgt']
            },
            canActivate: [AuthGuardService],
            providers: [
              StockPackingHistoryService,
              importProvidersFrom(
                EffectsModule.forFeature([StockPackingHistoryEffects]),
                StoreModule.forFeature(stockPackingHistoryKey, StockPackingHistoryReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/stock-packing-history/stock-packing-history-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          },
          {
            path: 'stock-collection-history',
            data: {
              isShowOnMenu: true,
              title: 'STOCK_COLLECTION_HISTORY',
              permissions: ['pm_recall_stkcoll_v'],
              featureName: ['store-stock-recall.warehouse-recall-mgt']
            },
            canActivate: [AuthGuardService],
            providers: [
              StockCollectionHistoryService,
              importProvidersFrom(
                EffectsModule.forFeature([StockCollectionHistoryEffects]),
                StoreModule.forFeature(stockCollectionHistoryKey, StockCollectionHistoryReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/stock-collection-history/stock-collection-history-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'RETURN_TO_SUPPLIER',
          isShowOnMenu: true,
          icon: 'icon-return-to-supplier',
          permissions: ['pm_return_to_sup_v']
        },
        children: [
          {
            path: 'return-to-supplier-history',
            data: {
              isShowOnMenu: true,
              title: 'RETURN_TO_SUPPLIER_HISTORY',
              permissions: ['pm_return_to_sup_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              ReturnToSupplierHistoryService,
              importProvidersFrom(
                EffectsModule.forFeature([ReturnToSupplierHistoryEffects]),
                StoreModule.forFeature(returnToSupplierHistoryKey, ReturnToSupplierHistoryReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/return-to-supplier-history/return-to-supplier-history-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'MEMBER_REWARD_PICKUP',
          isShowOnMenu: true,
          icon: 'icon-gift',
          permissions: ['pm_reward_pickup_v', 'pm_reward_pickup_m']
        },
        children: [
          {
            path: 'member-reward-pickup',
            data: {
              isShowOnMenu: true,
              title: 'MEMBER_REWARD_PICKUP',
              permissions: ['pm_reward_pickup_v', 'pm_reward_pickup_m']
            },
            providers: [
              MemberRewardPickupService,
              importProvidersFrom(
                EffectsModule.forFeature([MemberRewardPickupEffects]),
                StoreModule.forFeature(memberRewardPickupKey, memberRewardPickupReducer)
              )
            ],
            canActivate: [AuthGuardService],
            loadChildren: () =>
              import('./feature/member-reward-pickup/member-reward-pickup-routing.module').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'PRODUCT_TAG',
          isShowOnMenu: true,
          icon: 'icon-tag',
          permissions: ['pm_product_tag_m']
        },
        children: [
          {
            path: 'product-tag',
            data: {
              isShowOnMenu: true,
              title: 'PRODUCT_TAG',
              permissions: ['pm_product_tag_m']
            },
            canActivate: [AuthGuardService],
            loadChildren: () => import('./feature/product-tag/product-tag-routing').then((mod) => mod.CENTRAL_ROUTES)
          },
          {
            path: 'product-tag-printing-history',
            data: {
              isShowOnMenu: true,
              title: 'PRODUCT_TAG_PRINTING_HISTORY',
              permissions: ['pm_product_tag_m']
            },
            canActivate: [AuthGuardService],
            loadChildren: () =>
              import('./feature/product-tag-printing-history/product-tag-printing-history-routing').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'SHELF',
          isShowOnMenu: true,
          icon: 'icon-shelf',
          permissions: ['pm_shelf_v']
        },
        children: [
          {
            path: 'shelf',
            data: {
              isShowOnMenu: true,
              title: 'SHELF',
              permissions: ['pm_shelf_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              ShelfService,
              importProvidersFrom(
                EffectsModule.forFeature([ShelfEffects]),
                StoreModule.forFeature('shelf', shelfReducer)
              )
            ],
            loadChildren: () => import('./feature/shelf/shelf-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'MARKETING_CAMPAIGN',
          isShowOnMenu: true,
          icon: 'icon-marketing-campaign',
          permissions: ['pm_sup_coupon_v']
        },
        children: [
          {
            path: 'supplier-partner-coupon',
            data: {
              isShowOnMenu: true,
              title: 'SUPPLIER_PARTNER_COUPON',
              permissions: ['pm_sup_coupon_v']
            },
            canActivate: [AuthGuardService],
            providers: [
              SupplierPartnerCouponService,
              importProvidersFrom(
                EffectsModule.forFeature([SupplierPartnerCouponEffects]),
                StoreModule.forFeature(supplierPartnerCouponFeatureKey, supplierPartnerCouponReducer)
              )
            ],
            loadChildren: () =>
              import('./feature/supplier-partner-coupon/supplier-partner-coupon-routing').then(
                (mod) => mod.CENTRAL_ROUTES
              )
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'REDUCE_TO_CLEAR',
          isShowOnMenu: true,
          icon: 'icon-rtc',
          permissions: ['pm_rtc_req_v', 'pm_rtc_v']
        },
        children: [
          {
            path: 'rtc',
            data: {
              isShowOnMenu: true,
              title: 'REDUCE_TO_CLEAR',
              permissions: ['pm_rtc_req_v', 'pm_rtc_v']
            },
            providers: [
              ReduceToClearRequestService,
              importProvidersFrom(
                EffectsModule.forFeature([ReduceToClearRequestEffects]),
                StoreModule.forFeature('reduceToClearRequest', reduceToClearRequestReducer)
              ),
              ReduceToClearService,
              importProvidersFrom(
                EffectsModule.forFeature([ReduceToClearEffects]),
                StoreModule.forFeature('reduceToClear', reduceToClearReducer)
              ),
              ReduceToClearPermission
            ],
            loadChildren: () =>
              import('./feature/reduce-to-clear/reduce-to-clear-routing.module').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'DESTROY',
          isShowOnMenu: true,
          icon: 'icon-destroy',
          permissions: ['pm_dt_v', 'pm_dt_req_v']
        },
        children: [
          {
            path: 'destroy',
            data: {
              isShowOnMenu: true,
              title: 'DESTROY',
              permissions: ['pm_dt_v', 'pm_dt_req_v']
            },
            providers: [
              DestroyService,
              importProvidersFrom(
                EffectsModule.forFeature([DestroyEffects]),
                StoreModule.forFeature('destroy', destroyReducer)
              ),
              DestroyPermission
            ],
            loadChildren: () => import('./feature/destroy/destroy-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'TASK_ASSIGNMENT',
          isShowOnMenu: true,
          icon: 'icon-megaphone',
          permissions: ['pm_task_m', 'pm_task_v']
        },
        children: [
          {
            path: 'task-assignment',
            data: {
              isShowOnMenu: true,
              title: 'TASK_ASSIGNMENT',
              permissions: ['pm_task_m', 'pm_task_v']
            },
            providers: [TaskAssignmentPermission],
            loadChildren: () =>
              import('./feature/task-assignment/task-assignment-routing').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      },
      {
        path: '',
        data: {
          title: 'REPORT',
          isShowOnMenu: true,
          icon: 'icon-document-order'
        },
        children: [
          {
            path: 'report',
            data: {
              isShowOnMenu: true,
              title: 'REPORT',
              permissions: [
                'pm_rep_sales_perf_v',
                'pm_rep_saletx_v',
                'pm_rep_shift_rec_v',
                'pm_rep_store_exp_v',
                'pm_rep_store_oth_inc_v',
                'pm_rep_count_in_tote_v',
                'pm_rep_rtc_sum_v',
                'pm_rep_dt_v',
                'pm_rep_cafe_stock_v',
                'pm_rep_recall_pack_v',
                'pm_rep_recall_return_v',
                'pm_rep_material_req_v'
              ]
            },
            providers: [
              ReportService,
              importProvidersFrom(
                EffectsModule.forFeature([ReportsEffects, MyReportEffects]),
                StoreModule.forFeature('report', reportReducer),
                StoreModule.forFeature('myReport', myReportReducer)
              )
            ],
            loadChildren: () => import('./feature/report/report-routing.module').then((mod) => mod.CENTRAL_ROUTES)
          }
        ]
      }
    ]
  },
  // { path: 'auth', data: { breadcrumb: 'Auth' }, loadChildren: () => import('./feature/components/auth/auth.module').then(m => m.AuthModule) },
  {
    path: 'test-printer',
    // relay ไม่ใช่ API ของเรา — interceptor ของแอปแนบ header จนโดน CORS preflight, JSON.parse body ที่เป็น
    // plain text แล้วพัง และเตะออกหน้า login ตอนยังไม่ล็อกอิน route นี้จึงคุยกับ relay ผ่าน backend ตรง ๆ
    providers: [
      { provide: HttpClient, useFactory: (backend: HttpBackend) => new HttpClient(backend), deps: [HttpBackend] },
      RelayPrinterService
    ],
    loadComponent: () =>
      import('./feature/test-printer/test-printer.component').then((mod) => mod.TestPrinterComponent)
  },
  { path: '**', redirectTo: '' }
];

// @NgModule({
//   imports: [RouterModule.forRoot(routes, routerOptions)],
//   exports: [RouterModule]
// })
// export class AppRoutingModule {
// }
